import clsx from 'clsx'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  flex-shrink: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  gap: ${rem(16)};

  &.close-on-left {
    justify-content: flex-start;
  }

  &.has-content {
    justify-content: space-between;

    &.close-on-left {
      flex-direction: row-reverse;
    }
  }
`

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

interface Props {
  children?: ReactNode
  closeOnLeft?: boolean
  onClose: () => void
  onBack?: () => void
  onDelete?: () => void
  headerActions?: ReactNode
}

function TripDrawerHeader({
  children,
  closeOnLeft = false,
  onClose,
  onBack,
  onDelete,
  headerActions,
}: Props) {
  const intl = useIntl()
  const hasContent = !!children
  const showBackButton = !!onBack
  const showHeaderActions = !!headerActions || (!showBackButton && !!onDelete)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleDelete = useCallback(() => {
    closeDropdown()
    onDelete?.()
  }, [closeDropdown, onDelete])

  return (
    <Header className={clsx({ 'close-on-left': closeOnLeft, 'has-content': hasContent })}>
      {hasContent && <HeaderContent>{children}</HeaderContent>}
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8} fullWidth={!hasContent}>
        {showBackButton && (
          <TextButton
            kind="tertiary"
            size="medium"
            startIcon={<LineAngleLeftIcon />}
            onClick={onBack}
          >
            {intl.formatMessage({
              id: 'ZUjhekiHLl4vmxd11TN8',
              defaultMessage: 'Back',
              description: 'Back button label in trip drawer header in the trip planner feature',
            })}
          </TextButton>
        )}
        {!showBackButton && (
          <IconButton
            kind="tertiary"
            variant="dark"
            size="medium"
            onClick={onClose}
            aria-label={intl.formatMessage({
              id: '4fAcItd9fsE4Za7ZGt6U',
              defaultMessage: 'Close drawer',
              description: 'Accessibility label for button to close the trip drawer',
            })}
          >
            <LineTimesIcon />
          </IconButton>
        )}
        {showHeaderActions && (
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            {headerActions}
            {!showBackButton && onDelete && (
              <>
                <IconButton
                  kind="tertiary"
                  variant="dark"
                  size="medium"
                  onClick={toggleDropdown}
                  ref={dropdownTriggerRef}
                  aria-label={intl.formatMessage({
                    id: 'u5-AEWKWVERh503zCULl',
                    defaultMessage: 'More options',
                    description: 'Accessibility label for more options menu button in trip drawer',
                  })}
                >
                  <LineEllipsisVIcon />
                </IconButton>
                <DropdownList
                  size="S"
                  open={isDropdownOpen}
                  onClose={closeDropdown}
                  triggerRef={dropdownTriggerRef}
                  anchorRef={dropdownTriggerRef}
                >
                  <ClickableListItem
                    title={intl.formatMessage({
                      id: 'D8VHnehetJNe5buavMUs',
                      defaultMessage: 'Delete',
                      description: 'Delete action label in the trip drawer options menu',
                    })}
                    size="medium"
                    startIcon={<LineTrashAltIcon size="S" />}
                    onClick={handleDelete}
                  />
                </DropdownList>
              </>
            )}
          </Group>
        )}
      </Group>
    </Header>
  )
}

export default TripDrawerHeader
